import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import { Theme, makeStyles, Grid, Typography } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { NutrientIndicatorSlider } from "./nutrient-indicator-slider";
import { roundNumber } from "../../../../utils/round-number";
import { Wrench } from "../../../../assets/icons/wrench";
import { ChefHat } from "../../../../assets/icons/chef-hat";
import { Cutlery } from "../../../../assets/icons/cutlery";
import {
  getNutrientIndicationColorForValue,
  getNutrientIndicationDispositionStringForValue,
} from "../../../../utils/nutrient-indication-color";
import { GeneDataGroupType, NutrientsResponse } from "../../../../api/smartmelts-api/graphql/generated";
import { NutrientModal } from "../../../../components/modal/nutrient-modal";

interface INutrientIndicatorStyleProps {
  borderColor: string;
  hideRecommendation?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  base: {
    overflow: "hidden",
    maxWidth: "100%",
  },
  card: {
    border: (props: INutrientIndicatorStyleProps) => `solid ${props.borderColor}`,
    borderRadius: 16,
    overflow: "hidden",
    maxWidth: "100%",
  },
  indicator: {
    borderRadius: 90,
    width: 20,
    height: 20,
    marginRight: theme.spacing(2),
  },
  tooltip: {
    padding: 0,
    backgroundColor: "transparent",
  },
  cardHeader: {
    fontSize: 18,
    fontWeight: 700,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  cardHeaderContainer: {
    overflow: "hidden",
  },
  topContainer: {
    padding: theme.spacing(4),
  },
  bottomContainer: {
    display: ({ hideRecommendation }: INutrientIndicatorStyleProps) => (hideRecommendation ? "none" : "block"),
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(0)}px ${theme.spacing(2)}px`,
    backgroundColor: theme.palette.background.default,
  },
  cardBody: {
    display: "flex",
    flexFlow: "row wrap",
    overflowY: "hidden",
  },
  functionsBody: {
    whiteSpace: "nowrap",
  },
  functionText: {
    fontSize: 16,
    fontWeight: 300,
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  sourcesBody: {
    maxHeight: "24px",
    alignItems: "center",
  },
  recommendationsBody: {
    whiteSpace: "nowrap",
  },
  recommendationsText: {
    fontSize: 16,
    fontWeight: 300,
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  cardBodyText: {
    fontSize: 16,
    fontWeight: 300,
  },
  innerCardBody: {
    padding: `0 ${theme.spacing(1)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    borderRadius: `0 0 ${theme.spacing(4)}px ${theme.spacing(2)}px`,
  },
  arrow: {
    width: 12,
    height: 12,
  },
  wrench: {
    marginTop: theme.spacing(1),
  },
  infoButton: {
    cursor: "pointer",
  },
}));

interface INutrientIndicatorProps {
  nutrient: NutrientsResponse;
  geneDataGroupTypes?: GeneDataGroupType[];
  alwaysShowRda?: boolean;
  hideRecommendation?: boolean;
  borderColor?: string;
}

const formatValue = (value: number) => {
  return roundNumber(value, 2);
};

export const NutrientIndicator: FunctionComponent<INutrientIndicatorProps> = (props: INutrientIndicatorProps) => {
  const { t } = useTranslation();
  const { nutrient, geneDataGroupTypes, alwaysShowRda, hideRecommendation, borderColor } = props;
  const { value, name, rda, personalRda, unit } = nutrient;

  const [openNutrientModal, setOpenNutrientModal] = useState<boolean>(false);

  const color = useMemo(() => (borderColor ? borderColor : getNutrientIndicationColorForValue(+value)), [
    value,
    borderColor,
  ]);

  const dispositionString = useMemo(() => {
    return getNutrientIndicationDispositionStringForValue(Number(value), t);
  }, [value, t]);

  const classes = useStyles({ borderColor: color, hideRecommendation });

  const personalRdaPercentage = useMemo(() => personalRda && rda && (+personalRda / +rda) * 100, [personalRda, rda]);
  const hasRecommendation = useMemo(() => !isNaN(+personalRda!) && !isNaN(+rda!) && !!unit, [personalRda, rda, unit]);

  const sources = useMemo(() => [...nutrient.sources].sort((source) => source.sortOrder), [nutrient]);
  const sourceStrings = useMemo(
    () =>
      sources.map((source, index) =>
        index > 0 ? `, ${t(`nutrient.source_values.${source.name}`)}` : t(`nutrient.source_values.${source.name}`),
      ),
    [sources, t],
  );

  const nutrientFunctions = t(`nutrient.values.${nutrient.name}.functions`);

  const functionToShow = useMemo(() => {
    if (geneDataGroupTypes && geneDataGroupTypes.length) {
      return geneDataGroupTypes
        .filter((geneDataGroupType) => Object.keys(nutrientFunctions).includes(geneDataGroupType))
        .map((geneDataGroupType) => nutrientFunctions[geneDataGroupType])
        .slice(0, 1);
    } else if (typeof nutrientFunctions === "object") {
      return Object.values(nutrientFunctions).slice(0, 1);
    }
    return [];
  }, [geneDataGroupTypes, nutrientFunctions]);

  const handleNutrientModalClose = useCallback(() => {
    setOpenNutrientModal(false);
  }, [setOpenNutrientModal]);

  const handleClick = useCallback(() => {
    // pass nutrient
    setOpenNutrientModal(!openNutrientModal);
  }, [openNutrientModal]);

  return (
    <Grid container className={classes.base}>
      <Grid container item direction="column" className={classes.card}>
        <Grid item container className={classes.topContainer} direction="row">
          <Grid
            container
            item
            xs={12}
            justify="space-between"
            alignItems="center"
            wrap="nowrap"
            direction="row-reverse"
          >
            <Grid item className={classes.infoButton}>
              <InfoOutlined onClick={handleClick} />
            </Grid>
            <Grid item xs className={classes.cardHeaderContainer}>
              <Typography className={classes.cardHeader}>{t(`nutrient.values.${name}.name`)}</Typography>
            </Grid>
          </Grid>

          <Grid container item>
            <Grid item xs={12} className={`${classes.functionsBody} ${classes.cardBody}`}>
              <Grid item container className={classes.wrench} alignItems={"center"} wrap={"nowrap"}>
                <Wrench height="15" />
                <Grid item xs className={classes.functionText}>
                  <Typography component="span" className={classes.cardBodyText}>
                    {functionToShow ? functionToShow : t("micronutrient.not_specified")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} className={`${classes.sourcesBody} ${classes.cardBody}`}>
              <ChefHat height="15" />
              {sourceStrings.length > 0 &&
                sourceStrings.map((source, idx) => (
                  <Typography key={`${source}-${idx}`} component="span" className={classes.cardBodyText}>
                    {source}
                  </Typography>
                ))}
              {!sources.length && (
                <Typography component="span" className={classes.cardBodyText}>
                  {t("micronutrient.not_specified")}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} className={`${classes.recommendationsBody} ${classes.cardBody}`}>
              <Grid item container alignItems={"center"} wrap={"nowrap"}>
                <Cutlery height="15" />
                <Typography component="span" className={classes.recommendationsText}>
                  {hasRecommendation &&
                    `${formatValue(+personalRda!)} ${unit} (${formatValue(+personalRdaPercentage!)}% ${t(
                      "general.of",
                    )} Ø ${formatValue(+rda!)} ${unit})`}
                  {!hasRecommendation && alwaysShowRda && `${formatValue(+personalRda!)} ${unit}`}
                  {!hasRecommendation && !alwaysShowRda && t("micronutrient.rda_not_specified")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container className={classes.bottomContainer}>
          <Grid
            item
            container
            direction="row"
            justify="space-between"
            alignItems="center"
            className={classes.innerCardBody}
          >
            <Grid item xs={12} container direction="column">
              <Grid item container>
                <Grid item xs={12}>
                  <Typography style={{ fontSize: 14, fontWeight: 700 }}>
                    {t("nutrient.disposition.recommendation")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>{dispositionString}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <NutrientIndicatorSlider sliderColor={color} value={+value} disabled />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <NutrientModal onClose={handleNutrientModalClose} open={openNutrientModal} nutrient={nutrient} />
    </Grid>
  );
};
